/**
 * ONE PRICE BOOK — its dated rows, the act that adds one, and the act that publishes the book.
 *
 * ── WHY THIS READS LIKE A LEDGER AND NOT A SETTINGS PAGE ────────────────────────────────────────
 *
 * A price is not a field with a current value. 08 §11 #8 calls that "the single most important 'why
 * not a field'": a price on the SKU row cannot be changed without retroactively re-pricing closed
 * periods, which violates I5. A price is a DATED ROW; a change ADDS one.
 *
 * So superseded rows are shown, with their dates, beside the live ones. That is not clutter — it is
 * the answer to "what did this cost in March", which a commercial operator is asked as often as
 * "what does it cost", and which a screen showing only current values cannot give at all.
 *
 * ── AND WHAT IS DELIBERATELY ABSENT ─────────────────────────────────────────────────────────────
 *
 * No edit control and no delete control on any row, because the API offers neither. A price that
 * could be corrected in place is a closed period that can be re-priced.
 *
 * No zero price. `unit_price > 0` is a database CHECK and the service refuses it separately so the
 * refusal can say what it means: a free customer is a DISCOUNT — a dated term on
 * `subscription_discount` carrying an approver and a record (08 §13) — and the Owner's own 100%
 * waiver is exactly that. A zero list price would be a giveaway nobody approved.
 */

import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';

import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { ServerRead } from '../components/ServerRead';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import type { Column } from '../components/DataTable';
import type { PriceItemSummary } from '../api/contract';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { useServerData } from '../api/useServerData';
import { routes } from '../routes';

const TITLE_HE = 'מחירים בספר';

export interface PlatformPriceBookScreenProps {
  readonly priceBookUid: string;
}

export function PlatformPriceBookScreen({ priceBookUid }: PlatformPriceBookScreenProps): JSX.Element {
  const ctx = useWorkContext();

  const readItems = useCallback(
    () => callOperation('platform.price_book.items.read', { params: { price_book_uid: priceBookUid } }),
    [priceBookUid],
  );
  const itemsRead = useServerData(readItems);

  const [failure, setFailure] = useState<ActionFailure | null>(null);
  const [sending, setSending] = useState(false);
  /*
   * ══ PUBLISHING ASKS FIRST, AND IT DID NOT. 2026-10-30. ════════════════════════════════════════
   *
   * `publishBook` demotes the incumbent inside the same transaction — *"the demotion is 'whatever
   * is published now', so the code carries no memory of which book was chosen last"* — so one press
   * of a plain `btn` replaced THE PLATFORM'S OFFICIAL PRICE LIST, from the screen the Owner is
   * about to enter his opening prices on. The act was separate, which is what the list screen
   * promises; it was not CONFIRMED, which is a different promise.
   *
   * 09 §2.2 is the standard it failed, and the standard is not mine: *"the modal states plainly
   * what will happen, including the company and period it applies to. An external customer must
   * not have to infer the blast radius."* `Modal` already carries that contract, cancel already
   * takes the focus, and the shared component is used rather than a second confirmation grown here
   * — two copies of one rule drift in wording before anybody finds them wrong.
   *
   * NOT `irreversible`. Re-publishing the previous book restores it, and nothing already invoiced
   * is re-priced — `price_item` rows are dated and an invoice is a row. Claiming irreversibility
   * where there is none teaches an operator to discount the word on the screen where it is true.
   */
  const [confirmingPublish, setConfirmingPublish] = useState(false);
  /*
   * ══ AND THE SUCCESS HAD NO CARRIER EITHER. Found by RENDERING it, 2026-10-30. ════════════════
   *
   * With the confirmation in place I pressed it in a browser and the screen came back EXACTLY as it
   * was: modal gone, same table, nothing said. The failure arm has `ActionFailureNotice`; the
   * success arm had the reload, and a reload is invisible when the thing that changed is not on the
   * screen — this screen reads `items` and the published flag lives on the BOOK, which it never
   * reads. So the most consequential press on the page looked identical to a press that did
   * nothing, which is the state §5 names: an operator who cannot tell whether a write happened.
   *
   * jsdom could not have told me. It asserts a POST was sent, which is a different question from
   * whether a person can see that it was.
   *
   * IT IS HELD ON THE SCREEN, NOT INSIDE A PANEL, because `run` reloads immediately after and a
   * confirmation held by something that remounts appears and vanishes — measured on the billing
   * screen earlier today and not repeated here.
   */
  const [published, setPublished] = useState(false);
  /*
   * ══ AND SO DID THE OTHER BUTTON ON THE SAME SCREEN. **2026-10-31.** ══════════════════════════
   *
   * The reasoning above was applied to the press that publishes the book and not to the press
   * beside it, which is the one an operator makes five times in a row while entering an opening
   * catalogue. `addPrice` called `run` with no success arm at all, so a saved price said nothing
   * where the button is. The new row does reach the table above on a real server — but the form
   * sits BELOW that table, the table is sorted and already carries six rows, and "it is somewhere
   * in the grid behind you" is the same answer as none for a person looking at the button they
   * just pressed. The fix that was right one line up was right here too.
   */
  const [priceAdded, setPriceAdded] = useState(false);

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  function run(action: () => Promise<unknown>, onDone?: () => void): void {
    setSending(true);
    setFailure(null);
    /*
     * THE PREVIOUS ANSWER IS WITHDRAWN BEFORE A NEW WRITE, both of them. Two success notices now
     * live on this screen and only one write is ever in flight; leaving the other one standing
     * would let "הספר פורסם" sit above a price row that has just failed to save.
     */
    setPublished(false);
    setPriceAdded(false);
    void action()
      .then(
        () => {
          itemsRead.reload();
          onDone?.();
        },
        (err: unknown) => setFailure(classifyActionError(err)),
      )
      .finally(() => setSending(false));
  }

  function addPrice(e: FormEvent): void {
    e.preventDefault();
    const form = new FormData(e.target as HTMLFormElement);
    const planCode = String(form.get('planCode') ?? '').trim();
    const tierFrom = String(form.get('tierFrom') ?? '').trim();
    const tierTo = String(form.get('tierTo') ?? '').trim();

    run(
      () =>
        callOperation('platform.price_book.item.add', {
          params: { price_book_uid: priceBookUid },
          body: {
            skuCode: String(form.get('skuCode') ?? ''),
            // EMPTY MEANS "every plan" — a real answer, not a missing one.
            planCode: planCode === '' ? null : planCode,
            /*
             * ── AN EMPTIED BOX IS THE FIRST TIER, AND IT USED TO BE TIER ZERO. **2026-10-31.** ──
             *
             * `Number(form.get('tierFrom') ?? 1)` could never reach that `1`. A number field that
             * has been CLEARED yields `''`, not `null`, so `??` does not fire and `Number('')` is
             * **0** — and `services/price-book.ts:230` refuses `tier_from < 1` with
             * `invalid_tier`, which crosses the wire as a bare 400 and reaches the operator as the
             * generic error notice carrying a request id. Neither the label nor the refusal names
             * the field, so the only way out is to guess which box the server disliked.
             *
             * HOW NARROW THIS IS, SAID PLAINLY: the field carries `defaultValue={1}`, so the
             * ordinary path never hits it. It needs an operator who selects the 1 and deletes it —
             * which is what a person does when the neighbouring label tells them blank means no
             * limit and they take the pair to work the same way. `min={1}` does not catch it:
             * `min` constrains a value that is present and says nothing about an absent one.
             * Blank is now the 1 the route's own default already intended.
             */
            tierFrom: tierFrom === '' ? 1 : Number(tierFrom),
            tierTo: tierTo === '' ? null : Number(tierTo),
            // A STRING, straight from the field to a DECIMAL(18,2) column. Parsing it would round it.
            unitPrice: String(form.get('unitPrice') ?? ''),
            effectiveFrom: String(form.get('effectiveFrom') ?? ''),
          },
        }),
      () => setPriceAdded(true),
    );
  }

  const columns: readonly Column<PriceItemSummary>[] = [
    { key: 'sku', headerHe: 'מוצר', render: (i) => <span dir="ltr">{i.skuCode}</span> },
    {
      key: 'plan',
      headerHe: 'תכנית',
      // NULL is "every plan" and says so. A dash would read as missing data.
      render: (i) =>
        i.planCode === null ? <span>כל התכניות</span> : <span dir="ltr">{i.planCode}</span>,
    },
    {
      key: 'band',
      headerHe: 'מדרגה',
      render: (i) => (
        <span dir="ltr">
          {i.tierFrom}–{i.tierTo ?? '∞'}
        </span>
      ),
    },
    {
      key: 'price',
      headerHe: 'מחיר ליחידה',
      numeric: true,
      render: (i) => <span dir="ltr">{i.unitPrice}</span>,
    },
    {
      /*
       * ══ WHICH UNITS THAT RATE APPLIES TO. 0040. ═════════════════════════════════
       *
       * The Owner: *"ודא שהמנוע והממשק מגדירים אם המחיר חל על כל הכמות או רק על היחידות
       * שבמדרגה. קיום העמודות אינו סוגר את התהליך."* This column is the interface half of
       * that sentence: the band and the rate were already here, and what they MEAN was not.
       *
       * The words rather than the code, because `volume` on a Hebrew screen says nothing — and the
       * phrasing is the arithmetic: *"על כל הכמות"* against *"על היחידות שבמדרגה"*.
       */
      key: 'tierMode',
      headerHe: 'חל על',
      render: (i) =>
        i.tierMode === 'graduated' ? (
          <span>היחידות שבמדרגה בלבד</span>
        ) : i.tierMode === 'volume' ? (
          <span>כל הכמות</span>
        ) : (
          /* A value this build has no words for is shown as itself rather than mapped to the
             nearest familiar one — a wrong answer here is a wrong price. */
          <span dir="ltr">{i.tierMode}</span>
        ),
    },
    { key: 'from', headerHe: 'בתוקף מ־', render: (i) => <span dir="ltr">{i.effectiveFrom}</span> },
    {
      key: 'to',
      headerHe: 'עד',
      // A superseded row keeps its dates and keeps pricing the period it priced. This column is
      // the DATE only; whether the row is the live one is the next column, and it is not the
      // same question — see below.
      render: (i) =>
        i.effectiveTo === null ? <span className="tile__lead">ללא תאריך סיום</span> : <span dir="ltr">{i.effectiveTo}</span>,
    },
    {
      key: 'current',
      headerHe: 'המחיר הפעיל',
      /*
       * THE SERVER'S ANSWER, RENDERED. **F1R-02.**
       *
       * This column used to read `effectiveTo === null ? "בתוקף"`, which is right until somebody
       * enters next year's price: both rows are then open-ended, both were labelled live, and the
       * screen disagreed with what the product would actually charge.
       *
       * `isCurrent` comes from the same resolver that prices a sale. Nothing here decides it, and
       * nothing here may — a rule implemented twice is a rule that will differ once.
       */
      render: (i) =>
        i.isCurrent ? (
          <StatusChip labelHe="בתוקף" tone="ok" />
        ) : (
          <StatusChip labelHe="הוחלף" tone="muted" />
        ),
    },
  ];

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'ספרי מחירים', href: routes.platformPriceBooks }}
    >
      {failure === null ? null : (
        <ActionFailureNotice
          failure={failure}
          /*
           * THE FACTOR IS GIVEN HERE. **Owner section 3, 2026-10-27.**
           *
           * Without this handler `ActionFailureNotice` has no way to satisfy an MFA demand in
           * place and falls through to the arm that LINKS TO ANOTHER ADDRESS, which unmounts
           * this screen and loses whatever was half-entered. Every write this screen makes is
           * declared `per_action`, so the demand is a state it really reaches.
           *
           * NOTHING IS RE-SENT. `TenantUsersScreen` states the rule: *"A write that replays
           * itself after an authentication step is a write nobody pressed."* Clearing the
           * refusal puts the person back where they were, and they press again.
           */
          onStepUpSatisfied={() => setFailure(null)}
        />
      )}

      {!priceAdded ? null : (
        /*
         * WHAT IT SAYS IS WHAT THE WRITE DID, and not more: a row was added to THIS book. It is not
         * a price anybody pays yet — the book becomes the official list only when it is published,
         * which is the other button — and saying "the price was saved" without that would be read
         * as "the customer is now charged this".
         */
        <section className="notice">
          <h2 className="notice__title">שורת המחיר נוספה לספר</h2>
          <p className="notice__body">
            השורה נוספה לספר הזה ומופיעה בטבלה שמעל. היא אינה מתמחרת דבר עד שהספר יפורסם כמחירון
            הרשמי. מחיר קיים לא נערך ולא נמחק: שינוי מחיר הוא שורה נוספת עם תאריך תחילה מאוחר יותר.
          </p>
        </section>
      )}

      {!published ? null : (
        /*
         * THE ANSWER TO THE PRESS, IN THE OPEN. The default tone, not `warn`: nothing went wrong.
         * It states what changed rather than congratulating — an operator who publishes the wrong
         * book needs to read the consequence here and go and publish the right one.
         */
        <section className="notice">
          <h2 className="notice__title">הספר פורסם כמחירון הרשמי</h2>
          <p className="notice__body">
            מכאן ואילך כל מכירה חדשה מתומחרת מהספר הזה, וכל ספר שהיה מפורסם לפניו אינו הרשמי עוד.
            מחירים שכבר חויבו לא השתנו. אם פורסם הספר הלא נכון — פרסום הספר הנכון מחזיר את המצב.
          </p>
        </section>
      )}

      <ServerRead result={itemsRead.result} whatHe="את המחירים" skeletonRows={5}>
        {(data) => (
          <DataTable<PriceItemSummary>
            captionHe="שורות מחיר"
            columns={columns}
            rows={data.items}
            rowKey={(i) => `${i.skuCode}:${i.planCode ?? ''}:${i.tierFrom}:${i.effectiveFrom}`}
            filterSummaryHe={`מציג ${data.items.length} שורות מחיר, כולל שורות שהוחלפו`}
            emptyStateHe="אין עדיין מחירים בספר הזה. הקריאה לשרת הצליחה."
          />
        )}
      </ServerRead>

      {/* `signin__form` was the sign-in card's narrow column -- see the note on the list
          screen. Six fields down the middle of 420px is the long form the Owner reported. */}
      <form className="form" onSubmit={addPrice}>
        <div className="form__group form__group--wide">
        <h3 className="form__group-title">הוספת מחיר</h3>
        <p className="tile__lead">
          הוספה בלבד. מחיר קיים אינו נערך ואינו נמחק — שינוי מחיר הוא שורה חדשה עם תאריך תחילה
          מאוחר יותר, כדי שתקופה שכבר תומחרה לא תשתנה. מחיר אפס אינו מתקבל: פטור מלא הוא הנחה
          מאושרת ומתועדת, ולא מחירון.
        </p>
        <label className="field">
          <span className="field__label">קוד מוצר</span>
          <input className="field__input" name="skuCode" dir="ltr" required />
        </label>
        <label className="field">
          <span className="field__label">קוד תכנית — ריק פירושו כל התכניות</span>
          <input className="field__input" name="planCode" dir="ltr" />
        </label>
        <label className="field">
          <span className="field__label">מדרגה מ־</span>
          <input
            className="field__input"
            name="tierFrom"
            type="number"
            min={1}
            defaultValue={1}
            dir="ltr"
          />
        </label>
        <label className="field">
          <span className="field__label">מדרגה עד — ריק פירושו ללא הגבלה</span>
          <input className="field__input" name="tierTo" type="number" min={1} dir="ltr" />
        </label>
        <label className="field">
          <span className="field__label">מחיר ליחידה</span>
          <input className="field__input" name="unitPrice" dir="ltr" inputMode="decimal" required />
        </label>
        <label className="field">
          <span className="field__label">בתוקף מ־</span>
          <input className="field__input" name="effectiveFrom" type="date" dir="ltr" required />
        </label>
        <div className="actions-row">
          <button type="submit" className="btn btn--primary" disabled={sending}>
            הוספת מחיר
          </button>
        </div>
        </div>
      </form>

      <div className="actions-row">
        {/*
          PUBLISHING IS ITS OWN ACT, and it takes no fields: the book is the address, and a body
          carrying a date or a flag would be a second way to say the same thing. The press OPENS THE
          QUESTION; the modal is what sends it. See the state declaration above for why.
        */}
        <button
          type="button"
          className="btn"
          disabled={sending}
          onClick={() => setConfirmingPublish(true)}
        >
          פרסום הספר כמחירון הרשמי
        </button>
      </div>

      {!confirmingPublish ? null : (
        <Modal
          intentHe="פרסום הספר כמחירון הרשמי"
          /*
           * THE BLAST RADIUS, AND IT NAMES NO BOOK ON PURPOSE. The items read answers with `items`
           * and nothing else, so this screen does not hold the incumbent's name — and the writer
           * does not either: it demotes whatever is published, whichever that is. A sentence naming
           * a book here would be this screen's guess about a row it never read.
           */
          blastRadiusHe={
            'הספר הזה יהיה המחירון הרשמי. המחירון הרשמי הנוכחי — כל ספר שמפורסם כרגע — יחדל ' +
            'מלהיות הרשמי באותו רגע, וכל מכירה חדשה תתומחר מהספר הזה. מחירים שכבר חויבו אינם ' +
            'משתנים: שורת מחיר נושאת תאריכים וחשבונית היא רשומה. כדי לחזור, יש לפרסם מחדש את הספר ' +
            'הקודם.'
          }
          fields={0}
          confirmLabelHe={sending ? 'שולח…' : 'פרסום כמחירון הרשמי'}
          onConfirm={() => {
            setConfirmingPublish(false);
            run(
              () =>
                callOperation('platform.price_book.publish', {
                  params: { price_book_uid: priceBookUid },
                  body: {},
                }),
              () => setPublished(true),
            );
          }}
          onClose={() => setConfirmingPublish(false)}
        >
          {/*
            NO FIELDS. The book is the address and publishing takes no body — `fields={0}` is the
            declared count the invariant reads, and this paragraph is prose, not an input.
          */}
          <p className="tile__lead">
            אין מה למלא כאן. הספר נקבע לפי הכתובת, והפרסום אינו נושא תאריך או דגל.
          </p>
        </Modal>
      )}
    </AppShell>
  );
}
